// src/features/dashboard/teacher/AttendanceRegister.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import {
	fetchAttendanceForClass,
	setAttendance,
	fetchTermsForClass,
	fetchTopicOptions,
} from '../../../services/sheetDataService';
import AttendanceCellEditor from './AttendanceCellEditor';

// Helper: get day number (0=Sunday, 1=Monday...)
const getDayNumber = (dayName) => {
	const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
	return days.indexOf(dayName);
};

// ✅ CORRECTED: generate past session dates from term start (not just this week)
/*const generatePastSessionDates = (schedule, termStartDate) => {
	if (!schedule || schedule.length === 0) return [];

	const now = new Date();
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

	// Use term start as the beginning, or fallback to start of current week
	let start;
	if (termStartDate) {
		start = new Date(termStartDate);
		start.setHours(0, 0, 0, 0);
	} else {
		// Fallback to start of current week (Monday)
		const currentDay = now.getDay();
		start = new Date(today);
		start.setDate(today.getDate() - currentDay + (currentDay === 0 ? -6 : 1));
		start.setHours(0, 0, 0, 0);
	}

	const sessionDates = [];
	let currentDate = new Date(start);
	while (currentDate <= today) {
		const dayNum = currentDate.getDay(); // 0=Sunday, 1=Monday, etc.
		schedule.forEach((session) => {
			const sessionDayNum = getDayNumber(session.day); // convert "Monday" to 1, etc.
			if (sessionDayNum === dayNum) {
				const [hours, minutes] = session.startTime.split(':').map(Number);
				const sessionDateTime = new Date(currentDate);
				sessionDateTime.setHours(hours, minutes, 0, 0);

				// Only include sessions that have already occurred (past or now)
				if (sessionDateTime <= now) {
					sessionDates.push({
						date: currentDate.toISOString().slice(0, 10),
						day: session.day,
						startTime: session.startTime,
						duration: session.duration,
					});
				}
			}
		});
		currentDate.setDate(currentDate.getDate() + 1);
	}

	return sessionDates;
};*/

const generatePastSessionDates = (schedule, termStartDate) => {
	if (!schedule || schedule.length === 0) return [];

	const now = new Date();
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

	// Determine start date (local)
	let start;
	if (termStartDate) {
		// Parse YYYY-MM-DD as local date
		const parts = termStartDate.split('-').map(Number);
		start = new Date(parts[0], parts[1] - 1, parts[2]);
	} else {
		// Fallback: Monday of current week (local)
		const currentDay = now.getDay();
		start = new Date(today);
		start.setDate(today.getDate() - currentDay + (currentDay === 0 ? -6 : 1));
	}
	start.setHours(0, 0, 0, 0);

	const sessionDates = [];
	let currentDate = new Date(start);

	while (currentDate <= today) {
		const dayNum = currentDate.getDay(); // local day
		schedule.forEach((session) => {
			const sessionDayNum = getDayNumber(session.day); // "Monday" → 1
			if (sessionDayNum === dayNum) {
				const [hours, minutes] = session.startTime.split(':').map(Number);
				const sessionDateTime = new Date(currentDate);
				sessionDateTime.setHours(hours, minutes, 0, 0);

				// Only include sessions that have already occurred (past or now)
				if (sessionDateTime <= now) {
					// Format date manually as YYYY-MM-DD using local components
					const year = currentDate.getFullYear();
					const month = String(currentDate.getMonth() + 1).padStart(2, '0');
					const day = String(currentDate.getDate()).padStart(2, '0');
					sessionDates.push({
						date: `${year}-${month}-${day}`,
						day: session.day,
						startTime: session.startTime,
						duration: session.duration,
					});
				}
			}
		});
		currentDate.setDate(currentDate.getDate() + 1);
	}

	return sessionDates;
};

// Status colors and abbreviations
const STATUS_COLORS = {
	Present: 'bg-green-200',
	Absent: 'bg-red-200',
	'Arrive Late': 'bg-yellow-200',
	'Leave Early': 'bg-orange-200',
	'Late & Early': 'bg-purple-200',
	'No Class': 'bg-gray-300',
};

const STATUS_ABBREV = {
	Present: 'P',
	Absent: 'A',
	'Arrive Late': 'AL',
	'Leave Early': 'LE',
	'Late & Early': 'L&E',
	'No Class': 'NC',
};

const AttendanceRegister = ({ classItem, students, spreadsheetId }) => {
	const { accessToken, user } = useAuth();

	// Compute the correct sheet IDs
	const attendanceSheetId = spreadsheetId || user.attendanceSpreadsheetId || user.spreadsheetId;
	const topicsSheetId = user.topicsSpreadsheetId || user.spreadsheetId;

	const [attendanceMap, setAttendanceMap] = useState({});
	const [originalMap, setOriginalMap] = useState({});
	const [dateActivityMap, setDateActivityMap] = useState({});
	const [dates, setDates] = useState([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [selectedTerm, setSelectedTerm] = useState('');
	const [terms, setTerms] = useState([]);
	const [topicOptions, setTopicOptions] = useState([]);

	// Editor state
	const [editorOpen, setEditorOpen] = useState(false);
	const [editorStudent, setEditorStudent] = useState(null);
	const [editorDate, setEditorDate] = useState(null);
	const [editorStatus, setEditorStatus] = useState('Present');
	const [editorActivity, setEditorActivity] = useState('');

	// ✅ Stable student list fingerprint (prevents re-fetching on every parent render)
	const studentIds = useMemo(
		() => (students || []).map((s) => s.userId).sort().join(','),
		[students]
	);

	// ✅ Detect if this is a tutoring student (single student with studentType === 'tutor')
	const isTutor = useMemo(() => {
		return students && students.length === 1 && students[0]?.studentType === 'tutor';
	}, [students]);




	// ✅ FIX: Load terms/topics. Reset state immediately to prevent cross-contamination.
	/*useEffect(() => {
		// Reset terms and topics when switching subjects
		setTerms([]);
		setSelectedTerm('');
		setTopicOptions([]);

		const loadMetadata = async () => {
			if (!accessToken || !topicsSheetId || !classItem) return;
			try {
				const teacher = classItem.email || user.email;
				const subject = classItem.subject;
				const grade = classItem.grade;
				const termsData = await fetchTermsForClass(
					accessToken,
					topicsSheetId,
					teacher,
					subject,
					grade
				);
				setTerms(termsData);
				if (termsData.length > 0) {
					setSelectedTerm(termsData[0].term);
				}
				const options = await fetchTopicOptions(
					accessToken,
					topicsSheetId,
					teacher,
					subject,
					grade
				);
				setTopicOptions(options);
			} catch (err) {
				console.error('Error loading term/topic metadata:', err);
			}
		};
		loadMetadata();
	}, [accessToken, topicsSheetId, classItem?.email, classItem?.subject, classItem?.grade, user?.email]);
	// ✅ FIX: Load terms/topics. Reset state immediately to prevent cross-contamination.
	useEffect(() => {
		// Reset terms and topics when switching subjects
		setTerms([]);
		setSelectedTerm('');
		setTopicOptions([]);

		const loadMetadata = async () => {
			if (!accessToken || !topicsSheetId || !classItem) return;
			try {
				const teacher = classItem.email || user.email;
				const subject = classItem.subject;
				const grade = classItem.grade;

				let termsData = [];
				let termsSource = '';

				// STEP 1: Try to get terms from Topics sheet (primary source for group classes)
				try {
					const topicsTerms = await fetchTermsForClass(
						accessToken,
						topicsSheetId,
						teacher,
						subject,
						grade
					);
					if (topicsTerms && topicsTerms.length > 0) {
						termsData = topicsTerms;
						termsSource = 'Topics sheet';
					}
				} catch (err) {
					console.warn('Failed to fetch terms from Topics:', err);
				}

				// STEP 2: If no terms in Topics, try student schedule (fallback for ALL students)
				if (termsData.length === 0 && students && students.length > 0) {
					// Check if any student has termData
					const studentWithTerms = students.find(s => s.termData && s.termData.length > 0);
					if (studentWithTerms) {
						termsData = studentWithTerms.termData.map(t => ({
							term: t.term || '',
							termStart: t.termStart || '',
							termEnd: t.termEnd || '',
						}));
						termsSource = 'student schedule (fallback)';
					}
				}

				// STEP 3: If still no terms, log and leave empty
				if (termsData.length === 0) {
					console.log('📋 No terms found in Topics or student schedules.');
				} else {
					console.log(`📋 Using term data from ${termsSource}:`, termsData);
				}

				setTerms(termsData);
				if (termsData.length > 0) {
					setSelectedTerm(termsData[0].term);
				}

				// Topic options always come from Topics sheet (shared across all)
				const options = await fetchTopicOptions(
					accessToken,
					topicsSheetId,
					teacher,
					subject,
					grade
				);
				setTopicOptions(options);
			} catch (err) {
				console.error('Error loading term/topic metadata:', err);
			}
		};
		loadMetadata();
	}, [accessToken, topicsSheetId, classItem?.email, classItem?.subject, classItem?.grade, user?.email, students]);*/

	// ✅ FIX: Load terms/topics. Reset state immediately to prevent cross-contamination.
	/*useEffect(() => {
		// Reset terms and topics when switching subjects
		setTerms([]);
		setSelectedTerm('');
		setTopicOptions([]);

		const loadMetadata = async () => {
			if (!accessToken || !topicsSheetId || !classItem) return;
			try {
				const teacher = classItem.email || user.email;
				const subject = classItem.subject;
				const grade = classItem.grade;

				let termsData = [];
				let termsSource = '';

				// STEP 1: Try to use student's termData (for ALL students)
				if (students && students.length > 0) {
					const studentWithTerms = students.find(s => s.termData && s.termData.length > 0);
					if (studentWithTerms) {
						termsData = studentWithTerms.termData.map(t => ({
							term: t.term || '',
							termStart: t.termStart || '',
							termEnd: t.termEnd || '',
						}));
						termsSource = 'student schedule';
						console.log('📋 Using term data from student schedule:', termsData);
					}
				}

				// STEP 2: If no student has termData, and it's NOT a tutoring student, fetch from Topics
				const isTutor = students && students.length === 1 && students[0]?.studentType === 'tutor';
				if (termsData.length === 0 && !isTutor) {
					try {
						const topicsTerms = await fetchTermsForClass(
							accessToken,
							topicsSheetId,
							teacher,
							subject,
							grade
						);
						if (topicsTerms && topicsTerms.length > 0) {
							termsData = topicsTerms;
							termsSource = 'Topics sheet';
							console.log('📋 Using term data from Topics sheet:', termsData);
						}
					} catch (err) {
						console.warn('Failed to fetch terms from Topics:', err);
					}
				} else if (termsData.length === 0 && isTutor) {
					// For tutoring students with no termData, we explicitly do NOT fallback to Topics.
					console.log('📋 Tutoring student has no termData. Please capture term dates.');
					// Optionally set a state message to display in UI
					// setMessage('Please add term dates for this student.');
				}

				if (termsData.length === 0) {
					console.log('📋 No terms found.');
				} else {
					console.log(`📋 Using term data from ${termsSource}:`, termsData);
				}

				setTerms(termsData);
				if (termsData.length > 0) {
					setSelectedTerm(termsData[0].term);
				}

				// Topic options always come from Topics sheet (shared across all)
				const options = await fetchTopicOptions(
					accessToken,
					topicsSheetId,
					teacher,
					subject,
					grade
				);
				setTopicOptions(options);
			} catch (err) {
				console.error('Error loading term/topic metadata:', err);
			}
		};
		loadMetadata();
	}, [accessToken, topicsSheetId, classItem?.email, classItem?.subject, classItem?.grade, user?.email, students]);
*/

	// ✅ FIX: Load terms/topics. Reset state immediately to prevent cross-contamination.
	useEffect(() => {
		setTerms([]);
		setSelectedTerm('');
		setTopicOptions([]);

		const loadMetadata = async () => {
			if (!accessToken || !topicsSheetId || !classItem) return;
			try {
				const teacher = classItem.email || user.email;
				const subject = classItem.subject;
				const grade = classItem.grade;

				let termsData = [];
				let termsSource = '';

				// STEP 1: Try to use student's termData (for ALL students)
				if (students && students.length > 0) {
					const studentWithTerms = students.find(s => s.termData && s.termData.length > 0);
					if (studentWithTerms) {
						termsData = studentWithTerms.termData.map(t => ({
							term: t.term || '',
							termStart: t.termStart || '',
							termEnd: t.termEnd || '',
						}));
						termsSource = 'student schedule';
						console.log('📋 Using term data from student schedule:', termsData);
					}
				}

				// STEP 2: If no student has termData, and it's NOT a tutoring student, fetch from Topics
				const isTutor = students && students.length === 1 && students[0]?.studentType === 'tutor';
				if (termsData.length === 0 && !isTutor) {
					try {
						const topicsTerms = await fetchTermsForClass(
							accessToken,
							topicsSheetId,
							teacher,
							subject,
							grade
						);
						if (topicsTerms && topicsTerms.length > 0) {
							termsData = topicsTerms;
							termsSource = 'Topics sheet';
							console.log('📋 Using term data from Topics sheet:', termsData);
						}
					} catch (err) {
						console.warn('Failed to fetch terms from Topics:', err);
					}
				} else if (termsData.length === 0 && isTutor) {
					console.log('📋 Tutoring student has no termData. Please capture term dates.');
				}

				setTerms(termsData);
				if (termsData.length > 0) {
					setSelectedTerm(termsData[0].term);
				}

				const options = await fetchTopicOptions(
					accessToken,
					topicsSheetId,
					teacher,
					subject,
					grade
				);
				setTopicOptions(options);
			} catch (err) {
				console.error('Error loading term/topic metadata:', err);
			}
		};
		loadMetadata();
	}, [accessToken, topicsSheetId, classItem?.email, classItem?.subject, classItem?.grade, user?.email, studentIds]); // ✅ Changed from 'students' to 'studentIds'

	useEffect(() => {
		// Reset all attendance-related state to prevent carrying over old data
		setAttendanceMap({});
		setOriginalMap({});
		setDateActivityMap({});
		setDates([]);
		setLoading(true);

		const controller = new AbortController();



		/*const loadAttendance = async () => {
			// ⛔ CRITICAL GUARD: Don't fetch attendance until we have the correct term metadata.
			// This prevents using a different subject's term dates to generate the grid.
			if (
				!accessToken ||
				!attendanceSheetId ||
				!classItem ||
				!students ||
				students.length === 0 ||
				!selectedTerm ||
				terms.length === 0
			) {
				setLoading(false);
				return;
			}

			try {
				const records = await fetchAttendanceForClass(
					accessToken,
					attendanceSheetId,
					classItem.email || user.email,
					classItem.subject,
					classItem.grade
				);

				// If the component unmounted or switched subjects, ignore this response
				if (controller.signal.aborted) return;

				const map = {};
				const dateSet = new Set();
				const dateActivity = {};
				records.forEach((rec) => {
					const key = `${rec.userId}|${rec.date}`;
					map[key] = {
						status: rec.attendance || 'Present',
						activity: rec.activity || '',
					};
					dateSet.add(rec.date);
					if (!dateActivity[rec.date]) {
						dateActivity[rec.date] = rec.activity || '';
					}
				});

				const originalMapCopy = JSON.parse(JSON.stringify(map));

				// Generate past sessions using the CORRECT (current) term dates
				const schedule = classItem.schedule || [];
				const selectedTermData = terms.find((t) => t.term === selectedTerm);
				const termStart = selectedTermData?.termStart
					? new Date(selectedTermData.termStart)
					: null;
				const termEnd = selectedTermData?.termEnd
					? new Date(selectedTermData.termEnd)
					: null;

				const pastSessions = generatePastSessionDates(schedule, termStart);
				// Inside loadAttendance, after pastSessions is generated
				console.log('📊 DEBUG - loadAttendance:', {
					schedule: schedule,
					termStart: termStart,
					termEnd: termEnd,
					pastSessionsCount: pastSessions.length,
					pastSessions: pastSessions,
					dateSetSize: dateSet.size,
					studentsCount: students.length,
				});
				students.forEach((student) => {
					pastSessions.forEach((session) => {
						const sessionDate = new Date(session.date);
						if (selectedTermData && termStart && termEnd) {
							if (sessionDate < termStart || sessionDate > termEnd) return;
						}
						const key = `${student.userId}|${session.date}`;
						if (!map[key]) {
							map[key] = { status: 'Present', activity: '' };
							dateSet.add(session.date);
							if (!dateActivity[session.date]) {
								dateActivity[session.date] = '';
							}
						}
					});
				});

				// Only update state if the component is still mounted and not aborted
				if (!controller.signal.aborted) {
					setAttendanceMap(map);
					setOriginalMap(originalMapCopy);
					setDateActivityMap(dateActivity);
					setDates(Array.from(dateSet).sort());
				}
			} catch (err) {
				if (!controller.signal.aborted) {
					console.error('Error loading attendance:', err);
				}
			} finally {
				if (!controller.signal.aborted) {
					setLoading(false);
				}
			}

		};*/
		const loadAttendance = async () => {
			// ⛔ CRITICAL GUARD: Don't fetch attendance until we have the correct term metadata.
			if (
				!accessToken ||
				!attendanceSheetId ||
				!classItem ||
				!students ||
				students.length === 0 ||
				!selectedTerm ||
				terms.length === 0
			) {
				setLoading(false);
				return;
			}

			try {
				const records = await fetchAttendanceForClass(
					accessToken,
					attendanceSheetId,
					classItem.email || user.email,
					classItem.subject,
					classItem.grade
				);

				if (controller.signal.aborted) return;

				const map = {};
				const dateSet = new Set();
				const dateActivity = {};
				records.forEach((rec) => {
					const key = `${rec.userId}|${rec.date}`;
					map[key] = {
						status: rec.attendance || 'Present',
						activity: rec.activity || '',
					};
					dateSet.add(rec.date);
					if (!dateActivity[rec.date]) {
						dateActivity[rec.date] = rec.activity || '';
					}
				});

				const originalMapCopy = JSON.parse(JSON.stringify(map));

				// ✅ Get term start/end as strings (not Date objects)
				const schedule = classItem.schedule || [];
				const selectedTermData = terms.find((t) => t.term === selectedTerm);
				const termStart = selectedTermData?.termStart || null; // string like "2026-08-01"
				const termEnd = selectedTermData?.termEnd || null;     // string like "2026-08-31"

				// ✅ Pass the string directly
				const pastSessions = generatePastSessionDates(schedule, termStart);

				console.log('📊 DEBUG - loadAttendance:', {
					schedule: schedule,
					termStart: termStart,
					termEnd: termEnd,
					pastSessionsCount: pastSessions.length,
					pastSessions: pastSessions,
					dateSetSize: dateSet.size,
					studentsCount: students.length,
				});

				students.forEach((student) => {
					pastSessions.forEach((session) => {
						const sessionDate = new Date(session.date);
						// ✅ Convert to Date for comparison
						if (termStart && termEnd) {
							const startDate = new Date(termStart);
							const endDate = new Date(termEnd);
							if (sessionDate < startDate || sessionDate > endDate) return;
						}
						const key = `${student.userId}|${session.date}`;
						if (!map[key]) {
							map[key] = { status: 'Present', activity: '' };
							dateSet.add(session.date);
							if (!dateActivity[session.date]) {
								dateActivity[session.date] = '';
							}
						}
					});
				});

				if (!controller.signal.aborted) {
					setAttendanceMap(map);
					setOriginalMap(originalMapCopy);
					setDateActivityMap(dateActivity);
					setDates(Array.from(dateSet).sort());
				}
			} catch (err) {
				if (!controller.signal.aborted) {
					console.error('Error loading attendance:', err);
				}
			} finally {
				if (!controller.signal.aborted) {
					setLoading(false);
				}
			}
		};

		loadAttendance();
		return () => controller.abort();


	}, [accessToken, attendanceSheetId, classItem?.email, classItem?.subject, classItem?.grade, user?.email, selectedTerm, studentIds]);





	// ✅ Client-side date filtering based on selected term (instant dropdown switching)
	const filteredDates = useMemo(() => {
		const selectedTermData = terms.find((t) => t.term === selectedTerm);
		const termStart = selectedTermData?.termStart ? new Date(selectedTermData.termStart) : null;
		const termEnd = selectedTermData?.termEnd ? new Date(selectedTermData.termEnd) : null;

		if (selectedTermData && termStart && termEnd && dates.length > 0) {
			return dates.filter((d) => {
				const dObj = new Date(d);
				return dObj >= termStart && dObj <= termEnd;
			});
		}
		return dates;
	}, [dates, terms, selectedTerm]);

	// Open editor on cell click
	const handleCellClick = (student, date) => {
		if (!student) return;
		const key = `${student.userId}|${date}`;
		const data = attendanceMap[key] || { status: 'Present', activity: '' };
		setEditorStudent(student);
		setEditorDate(date);
		setEditorStatus(data.status);
		setEditorActivity(data.activity || dateActivityMap[date] || '');
		setEditorOpen(true);
	};

	const handleEditorSave = (status, activity) => {
		const date = editorDate;
		const studentKey = `${editorStudent.userId}|${date}`;
		const currentDateActivity = dateActivityMap[date] || '';

		// Update status for the selected student
		setAttendanceMap((prev) => ({
			...prev,
			[studentKey]: {
				status: status,
				activity: activity || currentDateActivity,
			},
		}));

		// If activity changed, ask to apply to all students on this date
		if (activity !== currentDateActivity) {
			if (
				window.confirm(
					`Apply "${activity}" as the activity for all students on ${new Date(
						date
					).toLocaleDateString()}?`
				)
			) {
				// Apply activity to all students on this date
				setAttendanceMap((prev) => {
					const updated = { ...prev };
					students.forEach((student) => {
						const key = `${student.userId}|${date}`;
						if (updated[key]) {
							updated[key] = {
								...updated[key],
								activity: activity || '',
							};
						} else {
							updated[key] = {
								status: 'Present',
								activity: activity || '',
							};
						}
					});
					return updated;
				});
				// Update dateActivityMap
				setDateActivityMap((prev) => ({ ...prev, [date]: activity || '' }));
			} else {
				// Revert the activity change for the single student
				setAttendanceMap((prev) => ({
					...prev,
					[studentKey]: {
						status: status,
						activity: currentDateActivity,
					},
				}));
			}
		}
	};

	// Save all changes
	const saveAttendance = async () => {
		if (!classItem) {
			alert('Class details are missing.');
			return;
		}
		if (!students || students.length === 0) {
			alert('No students to mark attendance for.');
			return;
		}
		if (filteredDates.length === 0) {
			alert('No dates available for the selected term. Please add a date first.');
			return;
		}

		const teacher = classItem.email || user.email;
		const subject = classItem.subject;
		const grade = classItem.grade;

		const changedRecords = [];
		const newRecords = [];

		students.forEach((student) => {
			filteredDates.forEach((date) => {
				const key = `${student.userId}|${date}`;
				const current = attendanceMap[key] || { status: 'Present', activity: '' };
				const original = originalMap[key];

				if (!original) {
					newRecords.push({
						student: `${student.name} ${student.surname}`,
						date: date,
						status: current.status,
						activity: current.activity || '',
					});
				} else if (
					current.status !== original.status ||
					current.activity !== original.activity
				) {
					changedRecords.push({
						student: `${student.name} ${student.surname}`,
						date: date,
						oldStatus: original.status,
						newStatus: current.status,
						oldActivity: original.activity,
						newActivity: current.activity,
					});
				}
			});
		});

		if (changedRecords.length === 0 && newRecords.length === 0) {
			alert('No changes to save.');
			return;
		}

		let message = '';
		if (newRecords.length > 0) {
			message += `New records will be created for:\n${newRecords
				.map(
					(r) =>
						`• ${r.student} on ${new Date(r.date).toLocaleDateString()}`
				)
				.join('\n')}\n\n`;
		}
		if (changedRecords.length > 0) {
			message += `The following records will be updated:\n${changedRecords
				.map(
					(r) =>
						`• ${r.student} on ${new Date(r.date).toLocaleDateString()}: ${r.oldStatus} → ${r.newStatus}${r.newActivity ? ` (Activity: ${r.newActivity})` : ''
						}`
				)
				.join('\n')}`;
		}
		if (!window.confirm(`Do you want to proceed?\n\n${message}`)) {
			return;
		}

		setSaving(true);
		let successCount = 0;
		let failCount = 0;
		const errors = [];

		try {
			const recordsToSave = [];

			[...newRecords, ...changedRecords].forEach((rec) => {
				const student = students.find(
					(s) => `${s.name} ${s.surname}` === rec.student
				);
				if (!student) return;
				const status = rec.status || rec.newStatus || 'Present';
				const activity = rec.activity || rec.newActivity || '';
				recordsToSave.push({
					userId: student.userId,
					organisationId: user.organisationId || '',
					name: student.name,
					surname: student.surname,
					email: student.email,
					school: user.school || 'N/A',
					province: user.province || 'N/A',
					district: user.district || 'N/A',
					circuit: user.circuit || 'N/A',
					teacher: teacher,
					subject: subject,
					grade: grade,
					term: selectedTerm,
					date: rec.date,
					attendance: status,
					activity: activity,
					gender: student.gender || '',
				});
			});

			for (const record of recordsToSave) {
				try {
					await setAttendance(accessToken, attendanceSheetId, record);
					successCount++;
				} catch (err) {
					failCount++;
					errors.push(
						`Failed for ${record.name} ${record.surname} on ${record.date}: ${err.message}`
					);
				}
			}

			const newOriginalMap = { ...originalMap };
			recordsToSave.forEach((rec) => {
				const key = `${rec.userId}|${rec.date}`;
				newOriginalMap[key] = { status: rec.attendance, activity: rec.activity };
			});
			setOriginalMap(newOriginalMap);

			let msg = `✅ Attendance saved: ${successCount} successful, ${failCount} failed.`;
			if (failCount > 0) {
				msg += `\n\nErrors:\n${errors.join('\n')}`;
			}
			alert(msg);
		} catch (err) {
			console.error('Save error:', err);
			alert('❌ Failed to save attendance: ' + (err.message || 'Unknown error'));
		} finally {
			setSaving(false);
		}
	};

	// Add today
	const addToday = () => {
		const today = new Date().toISOString().slice(0, 10);
		if (!dates.includes(today)) {
			setDates([...dates, today].sort());
		} else {
			alert('Today is already in the grid.');
		}
	};

	// Render a cell
	const renderCell = (student, date) => {
		const key = `${student.userId}|${date}`;
		const data = attendanceMap[key] || { status: 'Present', activity: '' };
		const status = data.status;
		const hasActivity = data.activity && data.activity.trim().length > 0;
		return (
			<td
				key={date}
				onClick={() => handleCellClick(student, date)}
				className={`border px-2 py-1 text-center cursor-pointer hover:opacity-75 ${STATUS_COLORS[status] || 'bg-gray-100'
					}`}
			>
				<div className="flex items-center justify-center gap-1">
					<span>{STATUS_ABBREV[status] || status}</span>
					{hasActivity && (
						<span className="text-xs text-gray-600" title={data.activity}>
							📝
						</span>
					)}
				</div>
			</td>
		);
	};

	// Render date header with activity and No Class toggle
	const renderDateHeader = (date) => {
		const activity = dateActivityMap[date] || '';
		const isNoClass = students.every((student) => {
			const key = `${student.userId}|${date}`;
			return attendanceMap[key] && attendanceMap[key].status === 'No Class';
		});

		const handleNoClassToggle = () => {
			if (isNoClass) {
				if (
					window.confirm(
						`Remove "No Class" status for ${new Date(
							date
						).toLocaleDateString()} and revert to Present?`
					)
				) {
					const updatedMap = { ...attendanceMap };
					students.forEach((student) => {
						const key = `${student.userId}|${date}`;
						if (updatedMap[key] && updatedMap[key].status === 'No Class') {
							updatedMap[key] = { status: 'Present', activity: activity };
						}
					});
					setAttendanceMap(updatedMap);
				}
			} else {
				if (
					window.confirm(
						`Mark ${new Date(date).toLocaleDateString()} as "No Class" for all students?`
					)
				) {
					const updatedMap = { ...attendanceMap };
					students.forEach((student) => {
						const key = `${student.userId}|${date}`;
						updatedMap[key] = { status: 'No Class', activity: '' };
					});
					setAttendanceMap(updatedMap);
					setDateActivityMap((prev) => ({ ...prev, [date]: '' }));
				}
			}
		};

		return (
			<th key={date} className="border px-2 py-1 text-center min-w-[80px] relative">
				<div>{new Date(date).toLocaleDateString()}</div>
				{activity && (
					<div className="text-xs text-gray-500 truncate max-w-[70px]" title={activity}>
						📝 {activity}
					</div>
				)}
				<button
					onClick={handleNoClassToggle}
					className={`text-xs mt-1 px-1 py-0.5 rounded ${isNoClass
						? 'bg-red-200 text-red-700 hover:bg-red-300'
						: 'bg-gray-200 text-gray-600 hover:bg-gray-300'
						}`}
					title={isNoClass ? 'Undo No Class' : 'Mark as No Class'}
				>
					{isNoClass ? 'Undo NC' : 'NC'}
				</button>
			</th>
		);
	};

	if (loading) return <div>Loading attendance...</div>;

	if (!students || students.length === 0) {
		return (
			<div className="text-center py-4 text-gray-500">
				No students enrolled in this class.
			</div>
		);
	}

	return (
		<div>
			<div className="flex justify-between items-center mb-4">
				<div className="flex items-center gap-4">
					<h3 className="text-lg font-semibold">Attendance Register</h3>
					{terms.length > 0 && (
						<select
							value={selectedTerm}
							onChange={(e) => setSelectedTerm(e.target.value)}
							className="border rounded px-2 py-1 text-sm"
						>
							{terms.map((t) => (
								<option key={t.term} value={t.term}>
									{t.term} ({t.termStart ? new Date(t.termStart).toLocaleDateString() : ''} -{' '}
									{t.termEnd ? new Date(t.termEnd).toLocaleDateString() : ''})
								</option>
							))}
						</select>
					)}
				</div>
				<div className="flex gap-2">
					<button
						onClick={addToday}
						className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
					>
						+ Add Today
					</button>
					<button
						onClick={saveAttendance}
						disabled={saving}
						className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
					>
						{saving ? 'Saving...' : 'Save All'}
					</button>
				</div>
			</div>

			{/* Legend */}
			<div className="flex flex-wrap gap-3 text-xs mb-2">
				<span>
					<span className="inline-block w-3 h-3 bg-green-200 rounded mr-1"></span> P (Present)
				</span>
				<span>
					<span className="inline-block w-3 h-3 bg-red-200 rounded mr-1"></span> A (Absent)
				</span>
				<span>
					<span className="inline-block w-3 h-3 bg-yellow-200 rounded mr-1"></span> AL (Arrive
					Late)
				</span>
				<span>
					<span className="inline-block w-3 h-3 bg-orange-200 rounded mr-1"></span> LE (Leave
					Early)
				</span>
				<span>
					<span className="inline-block w-3 h-3 bg-purple-200 rounded mr-1"></span> L&E (Late
					& Early)
				</span>
				<span>
					<span className="inline-block w-3 h-3 bg-gray-300 rounded mr-1"></span> NC (No Class)
				</span>
				<span>📝 = Activity set</span>
			</div>

			<div className="overflow-x-auto">
				<table className="w-full table-auto border-collapse text-sm">
					<thead>
						<tr className="bg-gray-100">
							<th className="border px-2 py-1 text-left sticky left-0 bg-gray-100">Student</th>
							<th className="border px-2 py-1 text-left bg-gray-100">Gender</th>
							{filteredDates.map((date) => renderDateHeader(date))}
						</tr>
					</thead>
					<tbody>
						{students.map((student) => (
							<tr key={student.userId} className="border-t">
								<td className="border px-2 py-1 sticky left-0 bg-white font-medium">
									{student.name} {student.surname}
								</td>
								<td className="border px-2 py-1 bg-white text-center">
									{student.gender || '—'}
								</td>
								{filteredDates.map((date) => renderCell(student, date))}
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{/* Cell Editor Modal */}
			<AttendanceCellEditor
				isOpen={editorOpen}
				onClose={() => setEditorOpen(false)}
				student={editorStudent}
				date={editorDate}
				currentStatus={editorStatus}
				currentActivity={editorActivity}
				topicOptions={topicOptions}
				onSave={handleEditorSave}
			/>
		</div>
	);
};

export default AttendanceRegister;